import { Transition as HLTransition } from '@headlessui/react';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/solid';
import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { getYear, isAfter, sub } from 'date-fns';
import {
  forwardRef,
  ForwardRefRenderFunction,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Result } from '~/components/ui/Result';
import { ListJobsQuery } from '~/gql/graphql';
import { formatDate, formatDateInTimeZone } from '~/helpers/formatDate';
import { getJobDateKey } from '~/components/job/formatDate';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { UNSCHEDULED_MARKER } from '~/components/sites/tabs/DateDisplay';
import type { ViewMode } from '~/routes/jobs';
import { useTranslation } from 'react-i18next';
import { CalendarEvent } from './CalendarEvent';
import { JobListItem } from './JobListItem';

type Props = {
  view: 'scheduled' | 'completed';
  jobs?: ListJobsQuery['jobBoard'];
  scrollId?: string;
  hasFilters?: boolean;
  isSmall: boolean;
  selected?: string[];
  toggleSelected?: (id: string, shift: boolean, single?: boolean) => void;
  isLoading?: boolean;
  isScrollingDown?: boolean;
  totalHeaderHeight?: number;
  shouldScrollToToday?: boolean;
  onScrollToTodayActiveChange?: (active: boolean) => void;
};

const CURRENT_YEAR = getYear(new Date()).toString();

/**
 * Get a formatted date for grouping
 */
function getDate(
  view: ViewMode | undefined,
  record: ListJobsQuery['jobBoard'][number]
): string | null {
  if (record.__typename === 'JobCalendarEvent') {
    const date =
      record.summary === 'Check-out' ? record.end : record.start || record.end;
    // Return formatted date if available, otherwise null
    return date ? formatDate(date, 'yyyy-MM-dd') : null;
  }

  if (record.__typename === 'Job') {
    if (view === 'completed') {
      if (!record.completedAt) {
        return UNSCHEDULED_MARKER; // Special marker for unscheduled completed jobs
      }
      // completedAt is a UTC timestamp, format it using property timezone
      return record.timeZone
        ? formatDateInTimeZone(
            record.completedAt,
            record.timeZone,
            'yyyy-MM-dd'
          )
        : formatDate(record.completedAt, 'yyyy-MM-dd');
    } else if (view === 'scheduled') {
      // For scheduled view, use property-local date fields
      // Prefer scheduledStartDate/scheduledEndDate (property-local strings)
      const dateKey =
        getJobDateKey(record, 'scheduledStart') ||
        getJobDateKey(record, 'scheduledEnd');
      if (dateKey) {
        return dateKey;
      }
      // Fallback to createdAt if no scheduled dates
      return record.createdAt
        ? formatDate(record.createdAt, 'yyyy-MM-dd')
        : null;
    } else {
      // Default view (e.g., 'all jobs' if such a view existed) or if view is undefined.
      // Fallback to createdAt.
      return record.createdAt
        ? formatDate(record.createdAt, 'yyyy-MM-dd')
        : null;
    }
  }

  // Default to null for any other __typename or if no specific date logic applies
  return null;
}

type JobEntry = ListJobsQuery['jobBoard'][number];

function calculateTodayButtonVisibility(
  jobs: ReadonlyArray<JobEntry> | undefined,
  items: ReadonlyArray<{ index: number }>,
  todayIndex: number
): boolean {
  // Don't show button if no valid today index or no jobs/items
  if (
    todayIndex < 0 ||
    !jobs ||
    jobs.length === 0 ||
    !items ||
    items.length === 0 ||
    todayIndex >= jobs.length
  ) {
    return false;
  }

  // Balanced logic: show button when today is outside the visible range with small buffer
  const start = items[0]?.index ?? 0;
  const end = items[items.length - 1]?.index ?? start;

  // Hide button if today is anywhere in the visible range or just outside it
  const isNearVisible = todayIndex >= start - 1 && todayIndex <= end + 1;

  return !isNearVisible;
}

const JobListComponent: ForwardRefRenderFunction<
  { scrollToJob: () => void; scrollToToday: () => void },
  Props
> = (
  {
    view,
    jobs: originalJobs,
    scrollId,
    hasFilters: _unusedHasFilters, // kept for API compatibility
    isSmall,
    selected,
    toggleSelected,
    isLoading,
    isScrollingDown,
    totalHeaderHeight,
    shouldScrollToToday,
    onScrollToTodayActiveChange,
  },
  ref
) => {
  void _unusedHasFilters;
  const { isMobile } = useBreakpoint();
  const [todayIndex, setTodayIndex] = useState(-1);
  const [scrollTargetIndex, setScrollTargetIndex] = useState<number | null>(
    null
  );
  const [justScrolledToToday, setJustScrolledToToday] = useState(false);

  const hasScrolledInitiallyRef = useRef(false);
  const previousViewRef = useRef<ViewMode | undefined>(undefined);
  const prevJobsKeyRef = useRef<string>('');
  const prevTodayViewRef = useRef<ViewMode | undefined>(undefined);
  const [isMountingPhase, setIsMountingPhase] = useState(true);
  const [hasInitialRendered, setHasInitialRendered] = useState(false);

  // Scroll guard for protecting scroll-to-today operations
  const scrollGuardActiveRef = useRef(false);
  const scrollGuardTimeoutRef = useRef<NodeJS.Timeout>();
  const targetScrollPositionRef = useRef<number>(0);

  // Add safeguard to prevent rapid scroll-to-today operations
  const scrollToTodayInProgressRef = useRef(false);

  // Track the last filter operation to prevent multiple scrolls for the same filter change
  const lastScrollToTodayTriggerRef = useRef<string>('');

  // Allow content to fade in quickly after initial render (works on all devices)
  useEffect(() => {
    const renderTimer = setTimeout(() => {
      setHasInitialRendered(true);
    }, 100);

    return () => {
      clearTimeout(renderTimer);
    };
  }, []);

  // Track component mount/unmount and view changes in a single effect
  useEffect(() => {
    if (!isMobile) return;

    // Track view changes and reset scroll state ONLY on actual view changes
    const isActualViewChange =
      previousViewRef.current !== view && previousViewRef.current !== undefined;

    if (isActualViewChange) {
      // Update refs first to prevent re-execution
      previousViewRef.current = view;

      // Reset scroll state on mobile ONLY for actual view changes
      hasScrolledInitiallyRef.current = false;
      setScrollTargetIndex(null);

      // Clear any active scroll guards to prevent interference
      scrollGuardActiveRef.current = false;
      if (scrollGuardTimeoutRef.current) {
        clearTimeout(scrollGuardTimeoutRef.current);
      }
      targetScrollPositionRef.current = 0;

      // Reset scroll position to top immediately on view change
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

      // Reset mounting phase for new view
      setIsMountingPhase(true);
    } else if (previousViewRef.current === undefined) {
      // Initial mount - just set the view reference
      previousViewRef.current = view;
      setIsMountingPhase(true);
    }

    // Give component time to settle before allowing today button to appear
    const mountTimer = setTimeout(() => {
      setIsMountingPhase(false);
    }, 1000); // 1 second grace period

    return () => {
      clearTimeout(mountTimer);
      // Don't reset mounting phase here to avoid interference
      // Clear any active scroll guard timeouts
      if (scrollGuardTimeoutRef.current) {
        clearTimeout(scrollGuardTimeoutRef.current);
      }
    };
  }, [isMobile, view]);

  // Mobile scroll guard effect to protect against unwanted scroll resets
  useEffect(() => {
    if (!isMobile) return;

    const scrollGuardHandler = () => {
      if (scrollGuardActiveRef.current) {
        const currentY = window.scrollY;

        // Protect against unwanted scroll changes during active protection
        const targetPosition = targetScrollPositionRef.current;
        if (currentY === 0 && targetPosition > 0) {
          window.scrollTo({
            top: targetPosition,
            left: 0,
            behavior: 'auto',
          });
        } else if (
          Math.abs(currentY - targetPosition) > 200 &&
          targetPosition > 0
        ) {
          window.scrollTo({
            top: targetPosition,
            left: 0,
            behavior: 'auto',
          });
        }
      }
    };

    // Use passive listener to monitor, not prevent
    window.addEventListener('scroll', scrollGuardHandler, { passive: true });

    return () => {
      window.removeEventListener('scroll', scrollGuardHandler);
    };
  }, [isMobile]);

  const scrollableRef = useRef<HTMLDivElement>(null);

  // Mobile-only: when leaving the JobList, reset scroll to top so the next
  // view initializes from a clean position without residual scroll offsets.
  useEffect(() => {
    if (!isMobile) return;
    return () => {
      try {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      } catch {
        // noop
      }
    };
  }, [isMobile]);

  // Process jobs to handle unscheduled items in completed view
  const jobs = useMemo(() => {
    if (view === 'completed' && originalJobs) {
      const unscheduledJobs = originalJobs.filter(
        (job) => job.__typename === 'Job' && !job.completedAt
      );
      const scheduledJobs = originalJobs.filter(
        (job) => !(job.__typename === 'Job' && !job.completedAt)
      );
      // Ensure consistent sorting for scheduled jobs if needed, or rely on original order
      return [...unscheduledJobs, ...scheduledJobs];
    }
    return originalJobs;
  }, [originalJobs, view]);

  // Hooks must be called unconditionally; derive options conditionally instead
  const windowVirt = useWindowVirtualizer({
    count: jobs?.length ?? 0,
    paddingStart: 0,
    paddingEnd: 250,
    estimateSize: () => 100,
    getItemKey: (i: number) => {
      const job = jobs?.[i];
      return job ? `${job.__typename}:${job.id}-${i}` : `empty-${i}`;
    },
  });
  const elementVirt = useVirtualizer({
    count: jobs?.length ?? 0,
    paddingStart: 36,
    paddingEnd: 250,
    estimateSize: () => 100,
    getItemKey: (i: number) => {
      const job = jobs?.[i];
      return job ? `${job.__typename}:${job.id}-${i}` : `empty-${i}`;
    },
    getScrollElement: () => scrollableRef.current,
  });
  const rowVirtualizer = isMobile ? windowVirt : elementVirt;

  // Consolidated scroll handling effect - handles both scrollId and shouldScrollToToday
  useEffect(() => {
    // Handle scrollId-based navigation
    if (!hasScrolledInitiallyRef.current && scrollId && jobs) {
      const jobIndex = jobs.findIndex((job) => job.id === scrollId);
      if (jobIndex > -1) {
        setScrollTargetIndex(jobIndex);
        hasScrolledInitiallyRef.current = true;
        return; // Exit early to avoid conflicting with shouldScrollToToday
      }
    }

    // Handle parent-triggered scroll to today
    if (
      shouldScrollToToday &&
      todayIndex >= 0 &&
      (view === 'scheduled' || view === 'completed') &&
      !scrollToTodayInProgressRef.current // Prevent multiple simultaneous scroll-to-today operations
    ) {
      // Create a unique identifier for this filter operation based on jobs and view
      const jobsKey =
        jobs
          ?.map((job) => job.id)
          .slice(0, 10)
          .join(',') || '';
      const filterOperationId = `${view}-${todayIndex}-${jobsKey}`;

      // Only scroll if this is a new filter operation
      if (lastScrollToTodayTriggerRef.current !== filterOperationId) {
        lastScrollToTodayTriggerRef.current = filterOperationId;
        scrollToTodayInProgressRef.current = true; // Mark as in progress

        // Signal to parent that scroll-to-today is starting
        onScrollToTodayActiveChange?.(true);

        if (isMobile) {
          // Use longer delay to ensure view change scroll reset completes first
          const timeoutId = setTimeout(() => {
            setScrollTargetIndex(todayIndex);
          }, 300);

          return () => {
            clearTimeout(timeoutId);
            scrollToTodayInProgressRef.current = false; // Reset on cleanup
            onScrollToTodayActiveChange?.(false); // Signal completion
          };
        } else {
          // Simple desktop scroll
          const timeoutId = setTimeout(() => {
            rowVirtualizer.scrollToIndex(todayIndex, { align: 'start' });
            scrollToTodayInProgressRef.current = false; // Reset after execution
            onScrollToTodayActiveChange?.(false); // Signal completion
          }, 0);

          return () => {
            clearTimeout(timeoutId);
            scrollToTodayInProgressRef.current = false; // Reset on cleanup
            onScrollToTodayActiveChange?.(false); // Signal completion
          };
        }
      }
    }
  }, [
    shouldScrollToToday,
    todayIndex,
    view,
    isMobile,
    rowVirtualizer,
    jobs,
    scrollId,
    onScrollToTodayActiveChange,
  ]);

  // Execute scroll operation when scrollTargetIndex is set
  useEffect(() => {
    if (
      scrollTargetIndex === null ||
      scrollTargetIndex < 0 ||
      !rowVirtualizer
    ) {
      return;
    }

    const targetIdxToScroll = scrollTargetIndex;

    if (isMobile) {
      // Complex mobile scroll logic with retry until items are measured
      let cancelled = false;
      const tryScroll = (attempt: number) => {
        if (cancelled) return;
        const itemsReady =
          rowVirtualizer.getVirtualItems().length > 0 ||
          targetIdxToScroll === 0;
        if (!itemsReady && attempt < 15) {
          setTimeout(() => tryScroll(attempt + 1), 40);
          return;
        }

        if (
          rowVirtualizer &&
          (itemsReady || rowVirtualizer.getVirtualItems().length > 0)
        ) {
          // First, scroll to the target index
          rowVirtualizer.scrollToIndex(targetIdxToScroll, { align: 'start' });

          // Then adjust for header height in a single operation
          if (totalHeaderHeight && totalHeaderHeight > 0) {
            // Use requestAnimationFrame to ensure the virtualizer scroll completes first
            requestAnimationFrame(() => {
              const currentScrollY = window.scrollY;
              const targetY = Math.max(0, currentScrollY - totalHeaderHeight);

              if (view === 'scheduled') {
                scrollGuardActiveRef.current = true;
                if (scrollGuardTimeoutRef.current) {
                  clearTimeout(scrollGuardTimeoutRef.current);
                }
              }

              const isInitialLoad = !hasScrolledInitiallyRef.current;

              // Use a single, smooth scroll to the final position
              window.scrollTo({
                top: targetY,
                left: 0,
                behavior: isInitialLoad ? 'auto' : 'smooth',
              });

              if (isInitialLoad) {
                hasScrolledInitiallyRef.current = true;
              }

              targetScrollPositionRef.current = targetY;

              // Set scroll guard timeout
              if (view === 'scheduled') {
                scrollGuardTimeoutRef.current = setTimeout(() => {
                  scrollGuardActiveRef.current = false;
                }, 1500);
              }
            });
          }

          setScrollTargetIndex(null);
          scrollToTodayInProgressRef.current = false; // Reset safeguard when scroll completes
          onScrollToTodayActiveChange?.(false); // Signal completion to parent
        }
      };

      const tid = setTimeout(() => tryScroll(0), 20);
      return () => {
        cancelled = true;
        clearTimeout(tid);
      };
    } else {
      // Simple desktop scroll logic (like original)
      const timerId = setTimeout(() => {
        rowVirtualizer.scrollToIndex(targetIdxToScroll, { align: 'start' });

        setScrollTargetIndex(null);
        scrollToTodayInProgressRef.current = false; // Reset safeguard when scroll completes
        onScrollToTodayActiveChange?.(false); // Signal completion to parent
      }, 0);

      return () => clearTimeout(timerId);
    }
  }, [scrollTargetIndex, rowVirtualizer, isMobile, totalHeaderHeight, view]);

  // Scroll to a specific job by scrollId - simplified callback
  const handleScrollToJob = useCallback(() => {
    if (jobs && scrollId && rowVirtualizer) {
      const index = jobs.findIndex((job) => job.id === scrollId);
      if (index > -1) {
        setScrollTargetIndex(index);
      }
    }
  }, [jobs, scrollId, rowVirtualizer]);

  const scrollToToday = useCallback(() => {
    if (isMobile) {
      // Complex mobile scroll logic
      if (todayIndex >= 0) {
        setScrollTargetIndex(todayIndex);
      }
    } else {
      // Simple desktop scroll - like original
      if (todayIndex >= 0) {
        rowVirtualizer.scrollToIndex(todayIndex, { align: 'start' });
      }
    }
  }, [todayIndex, isMobile, rowVirtualizer]);

  useImperativeHandle(
    ref,
    () => ({
      scrollToJob() {
        handleScrollToJob();
      },
      scrollToToday() {
        scrollToToday();
      },
    }),
    [handleScrollToJob, scrollToToday]
  );

  // Calculate today index for scroll-to-today functionality
  useEffect(() => {
    // Prevent recalculation if jobs or view haven't meaningfully changed
    const jobsKey = jobs?.map((job) => job.id).join(',') ?? '';

    // Allow recalculation if view changed, even if jobs are the same
    const isViewChange = prevTodayViewRef.current !== view;
    const isJobsChange = prevJobsKeyRef.current !== jobsKey;

    if (!isViewChange && !isJobsChange && prevJobsKeyRef.current !== '') {
      return; // Skip only if nothing has changed and we've run before
    }

    // Update refs
    prevJobsKeyRef.current = jobsKey;
    prevTodayViewRef.current = view;

    if (!jobs || jobs.length === 0) {
      setTodayIndex(-1);
      return;
    }

    const yesterday = sub(new Date(), { days: 1 });

    // Simplified findIndex logic - relies on getDate correctly handling view specifics
    const foundIndex = jobs.findIndex((job) => {
      if (!job) return false;
      const dateStr = getDate(view, job);

      // Ignore UNSCHEDULED items when looking for a date-based index
      if (!dateStr || dateStr === UNSCHEDULED_MARKER) return false;
      try {
        const jobDate = new Date(dateStr);
        if (isNaN(jobDate.getTime())) return false;
        const isAfterYesterday = isAfter(jobDate, yesterday);
        return isAfterYesterday;
      } catch {
        return false;
      }
    });

    let newTodayIndex = -1;
    if (foundIndex !== -1) {
      newTodayIndex = foundIndex;
    } else {
      // Fallback: if no jobs after yesterday, point to closest available
      if (view === 'completed') {
        newTodayIndex = jobs.length > 0 ? 0 : -1; // Point to newest completed job
      } else if (view === 'scheduled') {
        newTodayIndex = jobs.length > 0 ? jobs.length - 1 : -1; // Point to furthest scheduled job
      } else {
        newTodayIndex = -1;
      }
    }
    setTodayIndex(newTodayIndex);
  }, [jobs, view]);

  const items = rowVirtualizer.getVirtualItems();
  const start = rowVirtualizer.range?.startIndex ?? 0;
  const isArrowUp = start > todayIndex;

  const showTodayButton = calculateTodayButtonVisibility(
    jobs,
    items,
    todayIndex
  );

  // Enhanced today button visibility: hide during loading, scroll operations, and when near today
  // Button only shows when today is NOT currently visible (handled by showTodayButton)
  const finalShowTodayButton = isMobile
    ? !isLoading &&
      !isMountingPhase && // Don't show during initial mounting phase
      scrollTargetIndex === null && // Don't show during active scroll operations
      !justScrolledToToday && // Hide immediately after clicking today button
      showTodayButton && // Only show when today is NOT visible
      (view === 'scheduled' || view === 'completed') && // Show for scheduled and completed views
      todayIndex >= 0 // Only show when today index is valid
    : !justScrolledToToday && // Hide immediately after clicking today button
      showTodayButton && // Only show when today is NOT visible
      (view === 'scheduled' || view === 'completed') &&
      todayIndex >= 0; // Simplified for desktop

  const isAllSelected = jobs?.length === selected?.length;

  const renderJobListContent = () => {
    const firstItemStart = items[0]?.start ?? 0;

    return (
      <>
        <Result
          count={jobs?.length}
          className='absolute w-full pt-2' // Ensure z-index if needed
        />
        {/* Always render the sizing div now */}
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${firstItemStart}px)`,
            }}
          >
            {items.map((virtualItem) => {
              if (!jobs) return null;

              const i = virtualItem.index;
              const record = jobs[virtualItem.index];
              const { __typename } = record;
              const date = getDate(view, record);
              const prevDate = i === 0 ? undefined : getDate(view, jobs[i - 1]);
              const nextDate =
                i === 0 || i >= jobs.length - 1
                  ? undefined
                  : getDate(view, jobs[i + 1]);

              return (
                <div
                  className={classNames(
                    'flex whitespace-nowrap',
                    date !== prevDate ? 'lg:mt-[5px]' : isMobile ? '' : 'ml-24',
                    date !== nextDate &&
                      i !== 1 &&
                      date !== prevDate &&
                      'lg:min-h-[70px]',
                    isMobile ? 'flex-col' : 'items-center'
                  )}
                  key={String(virtualItem.key)}
                  data-index={virtualItem.index}
                  ref={rowVirtualizer.measureElement}
                >
                  {date !== prevDate && <DateDisplay date={date} />}
                  {__typename === 'Job' && (
                    <JobListItem
                      small={isSmall}
                      job={record}
                      selected={isAllSelected || selected?.includes(record.id)}
                      onSelect={(_, shift, single) =>
                        toggleSelected?.(record.id, shift, single)
                      }
                    />
                  )}
                  {__typename === 'JobCalendarEvent' && !record.hidden && (
                    <CalendarEvent small={isSmall} event={record} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {/* Container for the job list */}
      <div
        className={classNames(
          'relative max-w-full flex-1 overflow-x-hidden transition-opacity duration-300',
          !hasInitialRendered ? 'opacity-0' : 'opacity-100'
        )}
      >
        <div
          className={classNames(
            'pointer-events-none z-10 w-full transition-all duration-300 ease-in-out',
            !isMobile && 'absolute top-6' // Keep desktop style simple
          )}
          style={{
            // Apply fixed position and dynamic top only on mobile
            position: isMobile ? 'fixed' : undefined,
            // Use scroll direction and known heights for mobile top positioning
            top: isMobile
              ? isScrollingDown
                ? '1.25rem' // Fixed value (top-5) when scrolling down
                : `${totalHeaderHeight ?? 140}px` // Dynamic height when not scrolling down
              : undefined,
          }}
        >
          <HLTransition
            show={finalShowTodayButton}
            appear={true}
            enter='transition-opacity duration-75'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition-opacity duration-150'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <button
              className='pointer-events-auto mx-auto flex items-center gap-1 rounded-full border border-grey-20 bg-white px-3 py-1 text-sm shadow-md hover:bg-grey-5 lg:text-base'
              onClick={() => {
                setJustScrolledToToday(true);
                if (isMobile) {
                  setScrollTargetIndex(todayIndex);
                } else {
                  // Simple desktop scroll - like original
                  if (todayIndex >= 0) {
                    rowVirtualizer.scrollToIndex(todayIndex, {
                      align: 'start',
                    });
                  }
                }
                // Reset the flag after scroll settles
                setTimeout(() => setJustScrolledToToday(false), 500);
              }}
            >
              {isArrowUp ? (
                <ArrowUpIcon className='h-4' />
              ) : (
                <ArrowDownIcon className='h-4' />
              )}
              <span>Today</span>
            </button>
          </HLTransition>
        </div>
        {isMobile ? (
          // Mobile view: Container should NOT have explicit height
          <div className='relative max-w-full flex-1 gap-3 overflow-x-hidden p-3 pt-2'>
            {renderJobListContent()}
          </div>
        ) : (
          // Desktop view: Container has explicit height and overflow
          <div
            ref={scrollableRef}
            className='relative max-w-full flex-1 gap-3 overflow-x-hidden bg-surface-container-background pt-2 lg:px-4 lg:pb-0 lg:pl-0'
            style={{
              height: 'calc(100vh - 12.38rem)',
              overflowY: 'auto',
              contain: 'strict',
            }}
          >
            {renderJobListContent()}
          </div>
        )}
      </div>
    </>
  );
};

export const JobList = memo(forwardRef(JobListComponent));

// prettier-ignore
const months = [null, 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function DateDisplay({ date }: { date?: string | null }) {
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation();

  if (date === UNSCHEDULED_MARKER) {
    if (isMobile) {
      return (
        <div
          id='unscheduled-group'
          className='my-1 block w-full scroll-mt-12 bg-white p-1 font-semibold sm:left-2 lg:hidden lg:p-3 lg:text-xl xl:left-6'
        >
          {t('unscheduled')}
        </div>
      );
    } else {
      // Desktop view for "UNSCHEDULED" - display "To Plan"
      return (
        <div
          id='unscheduled-group'
          className='left-0 mx-6 -mt-3 hidden w-12 text-center lg:left-6 lg:block' // Standard desktop date display container style
        >
          <p className='text-lg font-semibold leading-none text-grey-50 lg:text-2xl lg:leading-none'>
            To
          </p>
          <p className='text-xs text-grey-40 lg:text-base lg:leading-tight'>
            Plan
          </p>
        </div>
      );
    }
  }

  const parts = date?.match(/^(\d\d\d\d)-(\d\d)-(\d\d)/);
  const displayYear = parts && CURRENT_YEAR !== parts[1] && ` ${parts[1]}`;

  if (isMobile) {
    return (
      <div
        id={date ? date.substring(0, 10) : 'date-group-mobile'}
        className='my-1 block w-full scroll-mt-12 bg-white p-1 font-semibold sm:left-2 lg:hidden lg:p-3 lg:text-xl xl:left-6'
      >
        {date ? formatDate(date, 'eee do MMM') : ''}{' '}
        {/* Avoid rendering 'Unscheduled' from formatDate here */}
        {displayYear}
      </div>
    );
  }
  return (
    <div
      id={date ? date.substring(0, 10) : 'date-group-desktop'}
      className='left-0 mx-6 -mt-3 hidden w-12 text-center lg:left-6 lg:block'
    >
      <p className='text-xs lg:text-base lg:leading-tight'>
        {date && formatDate(date, 'eee')}
      </p>
      <p className='text-lg font-semibold leading-none text-grey-50 lg:text-2xl lg:leading-none'>
        {parts ? parts[3] : 'To'}
      </p>
      <p className='text-xs text-grey-40 lg:text-base lg:leading-tight'>
        {parts ? months[parseInt(parts[2])] : 'Plan'}
        {displayYear ? ` ${displayYear.slice(-2)}` : null}
      </p>
    </div>
  );
}
